import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { vcollectFormsApi, vcollectChoiceListsApi, QUESTION_TYPE_LABELS, VCollectFormQuestion, VCollectFormGroup } from '../../api/vcollect';
import { vcollectSubmissionsApi } from '../../api/vcollect-submission';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { VERSION_STATUS_LABELS } from './VCollectFormsListPage';
import { LogicEditor } from './LogicEditor';

const EDITABLE_STATUSES = ['draft', 'returned_for_redraft'];
const CHOICE_TYPES = ['select_one', 'select_multiple'];
// Grouped for the type picker, matching how a form author actually thinks about these - not an alphabetical dump.
const TYPE_GROUPS: { label: string; types: string[] }[] = [
  { label: 'Text & Numbers', types: ['text', 'integer', 'decimal'] },
  { label: 'Choice', types: ['select_one', 'select_multiple'] },
  { label: 'Date & Time', types: ['date', 'time', 'datetime'] },
  { label: 'Media', types: ['image', 'audio', 'video', 'barcode'] },
  { label: 'Other', types: ['geopoint', 'note', 'calculate'] },
];

function slugify(base: string, existingNames: Set<string>): string {
  let n = 1;
  while (existingNames.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

export function FormBuilderPage() {
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const formId = Number(id);
  const versionIdNum = Number(versionId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  const { data: formData } = useQuery({ queryKey: ['vcollect-form', formId], queryFn: () => vcollectFormsApi.get(formId) });
  const form = formData?.data;
  const { data: versionData } = useQuery({ queryKey: ['vcollect-form-version', versionIdNum], queryFn: () => vcollectFormsApi.getVersion(versionIdNum) });
  const version = versionData?.data;
  const { data: questionsData } = useQuery({ queryKey: ['vcollect-questions', versionIdNum], queryFn: () => vcollectFormsApi.listQuestions(versionIdNum) });
  const questions = questionsData?.data ?? [];
  const { data: groupsData } = useQuery({ queryKey: ['vcollect-groups', versionIdNum], queryFn: () => vcollectFormsApi.listGroups(versionIdNum) });
  const groups = groupsData?.data ?? [];
  const { data: choiceListsData } = useQuery({ queryKey: ['vcollect-choice-lists'], queryFn: () => vcollectChoiceListsApi.list() });
  const choiceLists = choiceListsData?.data ?? [];

  const invalidateQuestions = () => queryClient.invalidateQueries({ queryKey: ['vcollect-questions', versionIdNum] });
  const invalidateGroups = () => queryClient.invalidateQueries({ queryKey: ['vcollect-groups', versionIdNum] });
  const invalidateVersion = () => queryClient.invalidateQueries({ queryKey: ['vcollect-form-version', versionIdNum] });

  const isEditable = version ? EDITABLE_STATUSES.includes(version.status) : false;
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const selectedQuestion = questions.find((q) => q.id === selectedQuestionId) ?? null;

  const [typePickerFor, setTypePickerFor] = useState<{ groupId: number | null } | null>(null);
  const [addingGroup, setAddingGroup] = useState<'section' | 'repeat' | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', label: '' });
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [showChoiceEditor, setShowChoiceEditor] = useState(false);

  const existingNames = new Set(questions.map((q) => q.name));

  /** Quick-add: creates the question immediately with a placeholder label and an auto-generated name, then selects it for inline editing - never a blocking modal in the middle of authoring. */
  const addQuestionMutation = useMutation({
    mutationFn: (input: { questionType: string; groupId: number | null }) => {
      const name = slugify(CHOICE_TYPES.includes(input.questionType) ? 'choice' : input.questionType, existingNames);
      const defaultChoiceListId = CHOICE_TYPES.includes(input.questionType) ? choiceLists[0]?.id : undefined;
      return vcollectFormsApi.addQuestion(versionIdNum, {
        name, questionType: input.questionType, label: 'Untitled question', groupId: input.groupId ?? undefined,
        choiceListId: defaultChoiceListId, displayOrder: questions.length,
      });
    },
    onSuccess: (r) => { setTypePickerFor(null); invalidateQuestions(); setSelectedQuestionId(r.data.id); },
    onError: (err: ApiError) => showError(err.message),
  });

  const addGroupMutation = useMutation({
    mutationFn: () => vcollectFormsApi.addGroup(versionIdNum, { name: groupForm.name, label: groupForm.label, isRepeat: addingGroup === 'repeat', displayOrder: groups.length }),
    onSuccess: () => { showSuccess(`${addingGroup === 'repeat' ? 'Repeat group' : 'Group'} added.`); setAddingGroup(null); setGroupForm({ name: '', label: '' }); invalidateGroups(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const deleteQuestionMutation = useMutation({
    mutationFn: (questionId: number) => vcollectFormsApi.deleteQuestion(versionIdNum, questionId),
    onSuccess: () => { setSelectedQuestionId(null); invalidateQuestions(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const updateQuestionMutation = useMutation({
    mutationFn: (input: Record<string, unknown>) => vcollectFormsApi.updateQuestion(versionIdNum, selectedQuestion!.id, input),
    onSuccess: () => invalidateQuestions(),
    onError: (err: ApiError) => showError(err.message),
  });

  const reorderQuestionsMutation = useMutation({
    mutationFn: (orderedIds: number[]) => vcollectFormsApi.reorderQuestions(versionIdNum, orderedIds),
    onSuccess: () => invalidateQuestions(),
    onError: (err: ApiError) => showError(err.message),
  });

  // ---- drag reordering (native HTML5 DnD - no extra library) ----
  const dragItemId = useRef<number | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  function handleDrop(targetId: number, scopeQuestions: VCollectFormQuestion[]) {
    setDragOverId(null);
    const sourceId = dragItemId.current;
    if (!sourceId || sourceId === targetId) return;
    const ids = scopeQuestions.map((q) => q.id);
    const from = ids.indexOf(sourceId); const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorderQuestionsMutation.mutate(ids);
  }

  const submitMutation = useMutation({ mutationFn: () => vcollectFormsApi.submitForReview(versionIdNum), onSuccess: () => { showSuccess('Submitted for review.'); invalidateVersion(); }, onError: (err: ApiError) => showError(err.message) });
  const approveMutation = useMutation({ mutationFn: () => vcollectFormsApi.approve(versionIdNum), onSuccess: () => { showSuccess('Approved.'); invalidateVersion(); }, onError: (err: ApiError) => showError(err.message) });
  const publishMutation = useMutation({ mutationFn: () => vcollectFormsApi.publish(versionIdNum), onSuccess: (r) => { showSuccess(r.data.note); invalidateVersion(); }, onError: (err: ApiError) => showError(err.message) });
  const [returnComment, setReturnComment] = useState('');
  const [showReturnBox, setShowReturnBox] = useState(false);
  const returnMutation = useMutation({ mutationFn: () => vcollectFormsApi.returnForRedraft(versionIdNum, returnComment), onSuccess: () => { showSuccess('Returned for redraft.'); setReturnComment(''); setShowReturnBox(false); invalidateVersion(); }, onError: (err: ApiError) => showError(err.message) });

  const [xformResult, setXformResult] = useState<{ xml: string; warnings: string[]; status: string } | null>(null);
  const generateXFormMutation = useMutation({
    mutationFn: () => vcollectFormsApi.generateXForm(versionIdNum),
    onSuccess: (r) => { setXformResult(r.data); setMoreMenuOpen(false); },
    onError: (err: ApiError) => showError(err.message),
  });

  const publishToOdkCentralMutation = useMutation({
    mutationFn: () => vcollectFormsApi.publishToOdkCentral(versionIdNum),
    onSuccess: (r) => { showSuccess(`Published to ODK Central as "${r.data.xmlFormId}".`); invalidateVersion(); },
    onError: (err: ApiError) => showError(err.message),
  });

  const syncSubmissionsMutation = useMutation({
    mutationFn: () => vcollectSubmissionsApi.sync(versionIdNum),
    onSuccess: (r) => {
      const { newCount, skippedCount, errors } = r.data;
      showSuccess(`${newCount} new submission(s) synced, ${skippedCount} already up to date${errors.length > 0 ? `, ${errors.length} failed` : ''}.`);
      setMoreMenuOpen(false);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const importXLSFormMutation = useMutation({
    mutationFn: (fileDataUri: string) => vcollectFormsApi.importXLSForm(versionIdNum, fileDataUri),
    onSuccess: (r) => { showSuccess(`Imported ${r.data.questionsImported} question(s), ${r.data.groupsImported} group(s).`); invalidateQuestions(); invalidateGroups(); setMoreMenuOpen(false); },
    onError: (err: ApiError) => showError(err.message),
  });
  const handleImportFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => importXLSFormMutation.mutate(reader.result as string);
    reader.readAsDataURL(file);
  };

  const addChoiceMutation = useMutation({
    mutationFn: ({ listId, value, label }: { listId: number; value: string; label: string }) => vcollectChoiceListsApi.addChoice(listId, { value, label }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vcollect-choices', selectedQuestion?.choice_list_id] }),
    onError: (err: ApiError) => showError(err.message),
  });

  if (!form || !version) return <div className="page"><div className="loading-state"><span className="loading-spinner" />Loading…</div></div>;

  const topLevelQuestions = questions.filter((q) => !q.group_id).sort((a, b) => a.display_order - b.display_order);

  function renderQuestionRow(q: VCollectFormQuestion, number: string, scope: VCollectFormQuestion[]) {
    const hasLogic = !!q.relevance_expression;
    const hasValidation = !!q.constraint_expression;
    return (
      <div key={q.id}>
        <div
          className={`fb-row${selectedQuestionId === q.id ? ' selected' : ''}${dragOverId === q.id ? ' drag-over' : ''}`}
          draggable={isEditable}
          onDragStart={() => (dragItemId.current = q.id)}
          onDragOver={(e) => { e.preventDefault(); setDragOverId(q.id); }}
          onDragLeave={() => setDragOverId((id) => (id === q.id ? null : id))}
          onDrop={() => handleDrop(q.id, scope)}
          onClick={() => setSelectedQuestionId(selectedQuestionId === q.id ? null : q.id)}
        >
          {isEditable && <span className="fb-row-handle">⋮⋮</span>}
          <span className="fb-row-number">{number}</span>
          <div className="fb-row-body">
            <div className="fb-row-label">{q.label || <span className="muted">Untitled question</span>}</div>
            <div className="fb-row-meta">
              <span className="fb-row-type">{QUESTION_TYPE_LABELS[q.question_type]}</span>
              <span className="fb-row-varname">{q.name}</span>
              {!!q.is_required && <span className="fb-indicator fb-indicator-required">Required</span>}
              {hasLogic && <span className="fb-indicator fb-indicator-logic">Skip logic</span>}
              {hasValidation && <span className="fb-indicator fb-indicator-validation">Validation</span>}
            </div>
          </div>
          {isEditable && (
            <span className="fb-row-menu-btn" onClick={(e) => { e.stopPropagation(); if (confirm(`Delete "${q.label}"?`)) deleteQuestionMutation.mutate(q.id); }} title="Delete question">✕</span>
          )}
        </div>
        {selectedQuestionId === q.id && isEditable && (
          <QuestionEditPanel
            question={q}
            allOtherQuestions={questions.filter((oq) => oq.id !== q.id)}
            choiceLists={choiceLists}
            onUpdate={(input) => updateQuestionMutation.mutate(input)}
            isSaving={updateQuestionMutation.isPending}
            showChoiceEditor={showChoiceEditor}
            setShowChoiceEditor={setShowChoiceEditor}
            onAddChoice={(listId, value, label) => addChoiceMutation.mutate({ listId, value, label })}
          />
        )}
      </div>
    );
  }

  function renderAddDivider(groupId: number | null) {
    if (!isEditable) return null;
    const key = groupId ?? 'top';
    return (
      <div style={{ position: 'relative' }}>
        <div className="fb-add-divider" onClick={() => setTypePickerFor(typePickerFor?.groupId === groupId ? null : { groupId })}>
          <span className="fb-add-divider-line" />
          <span className="fb-add-divider-label">+ Add Question</span>
          <span className="fb-add-divider-line" />
        </div>
        {typePickerFor?.groupId === groupId && (
          <div className="fb-type-picker" key={key}>
            {TYPE_GROUPS.map((g) => (
              <div key={g.label} style={{ display: 'contents' }}>
                <div className="fb-type-picker-section">{g.label}</div>
                {g.types.map((t) => {
                  const disabled = CHOICE_TYPES.includes(t) && choiceLists.length === 0;
                  return (
                    <div key={t} className="fb-type-picker-item" style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                         title={disabled ? 'Create a choice list first (Choice Lists page)' : undefined}
                         onClick={() => !disabled && addQuestionMutation.mutate({ questionType: t, groupId })}>
                      {QUESTION_TYPE_LABELS[t]}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page fb-page">
      <div className="fb-header">
        <div className="fb-header-title">
          <span className="fb-header-back" onClick={() => navigate(`/vcollect/forms/${formId}`)}>← {form.title}</span>
          <div className="fb-header-name-row">
            <span className="fb-header-name">v{version.version_number}</span>
            <StatusBadge status={version.status} label={VERSION_STATUS_LABELS[version.status]} />
            <span className="fb-header-meta">{questions.length} question{questions.length === 1 ? '' : 's'}</span>
          </div>
        </div>
        <div className="fb-header-actions">
          {version.status === 'draft' && <button className="fb-btn fb-btn-primary" onClick={() => submitMutation.mutate()} disabled={submitMutation.isPending || questions.length === 0}>Submit for Review</button>}
          {['submitted_for_review', 'under_review'].includes(version.status) && (
            <>
              <button className="fb-btn fb-btn-primary" onClick={() => approveMutation.mutate()} disabled={approveMutation.isPending}>Approve</button>
              {showReturnBox ? (
                <>
                  <input className="fb-btn" style={{ width: 160 }} placeholder="Reason for return…" value={returnComment} onChange={(e) => setReturnComment(e.target.value)} />
                  <button className="fb-btn" onClick={() => returnMutation.mutate()} disabled={returnMutation.isPending || !returnComment}>Confirm</button>
                </>
              ) : <button className="fb-btn" onClick={() => setShowReturnBox(true)}>Return for Redraft</button>}
            </>
          )}
          {version.status === 'approved' && <button className="fb-btn fb-btn-primary" onClick={() => publishMutation.mutate()}>Publish</button>}
          {version.status === 'active' && <button className="fb-btn fb-btn-primary" onClick={() => publishToOdkCentralMutation.mutate()} disabled={publishToOdkCentralMutation.isPending}>Push to ODK Central</button>}
          <div className="fb-more-menu">
            <button className="fb-btn" onClick={() => setMoreMenuOpen((o) => !o)}>Tools ⌄</button>
            {moreMenuOpen && (
              <div className="fb-more-dropdown" onMouseLeave={() => setMoreMenuOpen(false)}>
                <button onClick={() => generateXFormMutation.mutate()} disabled={generateXFormMutation.isPending || questions.length === 0}>Generate &amp; Validate XForm</button>
                <a href={vcollectFormsApi.exportXLSFormUrl(versionIdNum)} target="_blank" rel="noreferrer">Export XLSForm</a>
                <label style={{ cursor: isEditable ? 'pointer' : 'not-allowed' }}>
                  Import XLSForm
                  <input type="file" accept=".xlsx" style={{ display: 'none' }} disabled={!isEditable} onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); e.target.value = ''; }} />
                </label>
                {version.odk_xml_form_id && <button onClick={() => syncSubmissionsMutation.mutate()} disabled={syncSubmissionsMutation.isPending}>Sync Submissions</button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {version.status === 'returned_for_redraft' && version.return_comment && (
        <div className="warning-state" style={{ marginBottom: 10 }}><b>Returned for redraft:</b> {version.return_comment}</div>
      )}

      <div className="fb-layout">
        <div className="fb-sidebar">
          {isEditable && (
            <>
              <button className="fb-btn" style={{ display: 'block', width: '100%', marginBottom: 6 }} onClick={() => setAddingGroup('section')}>+ Group</button>
              <button className="fb-btn" style={{ display: 'block', width: '100%' }} onClick={() => setAddingGroup('repeat')}>+ Repeat Group</button>
            </>
          )}
        </div>

        <div className="fb-canvas">
          {questions.length === 0 && groups.length === 0 && !isEditable && <p className="muted" style={{ padding: 12 }}>No questions in this version.</p>}

          {renderAddDivider(null)}
          {topLevelQuestions.map((q, i) => renderQuestionRow(q, String(i + 1), topLevelQuestions))}

          {groups.sort((a, b) => a.display_order - b.display_order).map((g: VCollectFormGroup, gi) => {
            const groupQuestions = questions.filter((q) => q.group_id === g.id).sort((a, b) => a.display_order - b.display_order);
            return (
              <div key={g.id} className="fb-group-block">
                <div className="fb-group-header">
                  <span className="fb-group-icon">{g.is_repeat ? '↻' : '▤'}</span>
                  <span className="fb-group-label">{gi + 1}. {g.label}</span>
                  <span className="fb-group-type">{g.is_repeat ? 'Repeat Group' : 'Group'}</span>
                </div>
                <div className="fb-group-body">
                  {groupQuestions.map((q, qi) => renderQuestionRow(q, `${gi + 1}.${qi + 1}`, groupQuestions))}
                  {renderAddDivider(g.id)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {addingGroup && (
        <Modal title={addingGroup === 'repeat' ? 'Add Repeat Group' : 'Add Group'} onClose={() => setAddingGroup(null)}>
          <p className="muted">{addingGroup === 'repeat' ? 'A repeat group collects the same set of questions once per instance - for example, once per household member.' : 'A group organizes questions into a labeled section, such as "Household Information".'}</p>
          <label>Group Name (no spaces) <input value={groupForm.name} onChange={(e) => setGroupForm((s) => ({ ...s, name: e.target.value }))} placeholder="e.g. household_members" /></label>
          <label>Group Label <input value={groupForm.label} onChange={(e) => setGroupForm((s) => ({ ...s, label: e.target.value }))} placeholder="e.g. Household Members" /></label>
          <div className="form-actions">
            <button onClick={() => addGroupMutation.mutate()} disabled={addGroupMutation.isPending || !groupForm.name || !groupForm.label}>Add {addingGroup === 'repeat' ? 'Repeat Group' : 'Group'}</button>
          </div>
        </Modal>
      )}

      {xformResult && (
        <Modal title="Generated XForm — Structural Validation" onClose={() => setXformResult(null)}>
          <div className={xformResult.status === 'valid' ? 'tip-box' : 'warning-state'} style={{ marginBottom: 12 }}>
            <b>{xformResult.status === 'valid' ? 'Structurally valid, no warnings.' : `${xformResult.warnings.length} warning(s):`}</b>
            {xformResult.warnings.length > 0 && (
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {xformResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            This confirms the XForm is well-formed and internally consistent. It does <b>not</b> confirm compatibility with ODK Collect or ODK Central — that requires testing against real ODK infrastructure, which is not yet connected.
          </p>
          <pre style={{ background: 'var(--color-page-bg)', padding: 12, borderRadius: 4, maxHeight: 320, overflow: 'auto', fontSize: 11 }}>{xformResult.xml}</pre>
        </Modal>
      )}
    </div>
  );
}

interface QuestionEditPanelProps {
  question: VCollectFormQuestion;
  allOtherQuestions: VCollectFormQuestion[];
  choiceLists: { id: number; name: string }[];
  onUpdate: (input: Record<string, unknown>) => void;
  isSaving: boolean;
  showChoiceEditor: boolean;
  setShowChoiceEditor: (v: boolean) => void;
  onAddChoice: (listId: number, value: string, label: string) => void;
}

/** Inline expandable editing (Kobo-style) directly under the selected row - never a separate modal for ordinary edits. Label/hint save on blur; required toggles immediately. */
function QuestionEditPanel({ question, allOtherQuestions, choiceLists, onUpdate, isSaving, showChoiceEditor, setShowChoiceEditor, onAddChoice }: QuestionEditPanelProps) {
  const [label, setLabel] = useState(question.label);
  const [hint, setHint] = useState(question.hint ?? '');
  const { data: choicesData } = useQuery({
    queryKey: ['vcollect-choices', question.choice_list_id],
    queryFn: () => vcollectChoiceListsApi.listChoices(question.choice_list_id!),
    enabled: !!question.choice_list_id && showChoiceEditor,
  });
  const choices = choicesData?.data ?? [];
  const [newChoiceValue, setNewChoiceValue] = useState('');
  const [newChoiceLabel, setNewChoiceLabel] = useState('');
  const isChoiceType = CHOICE_TYPES.includes(question.question_type);
  const currentList = choiceLists.find((l) => l.id === question.choice_list_id);

  return (
    <div className="fb-edit-panel" onClick={(e) => e.stopPropagation()}>
      <div className="fb-edit-section">
        <span className="fb-edit-label">Question Label</span>
        <div className="fb-edit-field">
          <input value={label} onChange={(e) => setLabel(e.target.value)} onBlur={() => label !== question.label && onUpdate({ label })} />
        </div>
        <div className="fb-edit-row">
          <div className="fb-edit-field">
            <span className="fb-edit-label">Data Column Name</span>
            <input value={question.name} disabled title="Name cannot be changed after creation - other logic may reference it." />
          </div>
          <div className="fb-edit-field">
            <span className="fb-edit-label">Type</span>
            <input value={QUESTION_TYPE_LABELS[question.question_type]} disabled title="Delete and re-add the question to change its type." />
          </div>
        </div>
        <div className="fb-edit-field">
          <span className="fb-edit-label">Hint</span>
          <input value={hint} onChange={(e) => setHint(e.target.value)} onBlur={() => hint !== (question.hint ?? '') && onUpdate({ hint })} placeholder="Optional guidance shown below the question" />
        </div>
        <label className="fb-edit-checkbox">
          <input type="checkbox" checked={!!question.is_required} onChange={(e) => onUpdate({ isRequired: e.target.checked })} disabled={isSaving} /> Required
        </label>
      </div>

      {isChoiceType && (
        <div className="fb-edit-section">
          <span className="fb-edit-label">Options {currentList ? `(${currentList.name})` : ''}</span>
          {!showChoiceEditor ? (
            <button className="fb-btn" onClick={() => setShowChoiceEditor(true)}>Show / Add Options</button>
          ) : (
            <div>
              {choices.map((c) => <div key={c.id} className="fb-option-row"><span style={{ flex: 1, fontSize: 12.5 }}>{c.label} <span className="muted">({c.value})</span></span></div>)}
              <div className="fb-option-row">
                <input placeholder="value" value={newChoiceValue} onChange={(e) => setNewChoiceValue(e.target.value)} style={{ maxWidth: 100 }} />
                <input placeholder="Label" value={newChoiceLabel} onChange={(e) => setNewChoiceLabel(e.target.value)} />
                <button className="fb-btn" disabled={!newChoiceValue || !newChoiceLabel} onClick={() => { onAddChoice(question.choice_list_id!, newChoiceValue, newChoiceLabel); setNewChoiceValue(''); setNewChoiceLabel(''); }}>+ Add</button>
              </div>
              <p className="muted" style={{ fontSize: 11 }}>This choice list may be shared with other questions/forms - adding an option here adds it everywhere the list is used. Removing options is not yet supported here.</p>
            </div>
          )}
        </div>
      )}

      <div className="fb-edit-section">
        <span className="fb-edit-label">Logic &amp; Validation</span>
        <LogicEditor
          question={question}
          otherQuestions={allOtherQuestions}
          isSaving={isSaving}
          onSaveRelevance={(expr) => onUpdate({ relevanceExpression: expr })}
          onClearRelevance={() => onUpdate({ relevanceExpression: '' })}
          onSaveConstraint={(expr, msg) => onUpdate({ constraintExpression: expr, constraintMessage: msg })}
          onClearConstraint={() => onUpdate({ constraintExpression: '', constraintMessage: '' })}
        />
      </div>
    </div>
  );
}
